import {
  encodeMessage,
  type ClientMessage,
  type ClientRoomState,
  type ContentMode,
  type DesignSubmission,
  type ErrorCode,
  type GuessMap,
  type ServerMessage,
} from "@poppycock/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { clearSession, loadSession, saveSession } from "../lib/storage";
import { buildRoomWsUrl } from "../lib/ws-url";

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

export interface GameSocket {
  status: ConnectionStatus;
  state: ClientRoomState | null;
  playerId: string | null;
  lastError: { code: ErrorCode; message: string } | null;
  clearError: () => void;
  createRoom: (name: string) => void;
  joinRoom: (code: string, name: string) => void;
  setContentMode: (contentMode: ContentMode) => void;
  startGame: (contentMode?: ContentMode) => void;
  submitAnswer: (text: string) => void;
  submitDesign: (design: DesignSubmission) => void;
  submitGuesses: (guesses: GuessMap) => void;
  voteFunniest: (answerId: string) => void;
  nextRound: () => void;
  endGame: () => void;
  returnToLobby: () => void;
  leave: () => void;
  reconnect: () => void;
}

export function useGameSocket(): GameSocket {
  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [state, setState] = useState<ClientRoomState | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [lastError, setLastError] = useState<{
    code: ErrorCode;
    message: string;
  } | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const pendingRef = useRef<ClientMessage | null>(null);
  const intentionalClose = useRef(false);
  const pingTimer = useRef<number | null>(null);

  const clearError = useCallback(() => setLastError(null), []);

  const cleanupSocket = useCallback(() => {
    if (pingTimer.current) {
      window.clearInterval(pingTimer.current);
      pingTimer.current = null;
    }
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      ws.onopen = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close();
      }
    }
  }, []);

  const handleServerMessage = useCallback((raw: string) => {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(raw) as ServerMessage;
    } catch {
      return;
    }

    switch (msg.type) {
      case "welcome":
        setPlayerId(msg.playerId);
        break;
      case "room_state":
        setState(msg.state);
        setPlayerId(msg.state.you.id);
        saveSession({
          code: msg.state.code,
          playerId: msg.state.you.id,
          name: msg.state.you.name,
        });
        break;
      case "error":
        // Stale session after server restart / empty-room wipe — silent on home.
        if (
          msg.code === "room_not_found" ||
          msg.code === "not_in_room"
        ) {
          clearSession();
          setState(null);
          setPlayerId(null);
          setLastError(null);
          break;
        }
        setLastError({ code: msg.code, message: msg.message });
        break;
      case "pong":
        break;
    }
  }, []);

  const openSocket = useCallback(
    (code: string | "new", onOpenMsg?: ClientMessage) => {
      intentionalClose.current = false;
      cleanupSocket();
      setStatus("connecting");
      setLastError(null);

      const url = buildRoomWsUrl(code === "new" ? undefined : code);
      const ws = new WebSocket(url);
      wsRef.current = ws;
      pendingRef.current = onOpenMsg ?? null;

      ws.onopen = () => {
        setStatus("connected");
        pingTimer.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(encodeMessage({ type: "ping" }));
          }
        }, 25000);

        if (pendingRef.current) {
          ws.send(encodeMessage(pendingRef.current));
          pendingRef.current = null;
        }
      };

      ws.onmessage = (ev) => {
        if (typeof ev.data === "string") handleServerMessage(ev.data);
      };

      ws.onerror = () => {
        setStatus("error");
        setLastError({
          code: "internal",
          message: "Connection failed. Is the server running?",
        });
      };

      ws.onclose = () => {
        if (pingTimer.current) {
          window.clearInterval(pingTimer.current);
          pingTimer.current = null;
        }
        if (!intentionalClose.current) {
          setStatus("disconnected");
        } else {
          setStatus("idle");
        }
      };
    },
    [cleanupSocket, handleServerMessage],
  );

  const send = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setLastError({
        code: "internal",
        message: "Not connected.",
      });
      return;
    }
    ws.send(encodeMessage(msg));
  }, []);

  const createRoom = useCallback(
    (name: string) => {
      clearSession();
      setState(null);
      setLastError(null);
      openSocket("new", { type: "create_room", name });
    },
    [openSocket],
  );

  const joinRoom = useCallback(
    (code: string, name: string) => {
      setState(null);
      setLastError(null);
      openSocket(code, { type: "join_room", code, name });
    },
    [openSocket],
  );

  const setContentMode = useCallback(
    (contentMode: ContentMode) =>
      send({ type: "set_content_mode", contentMode }),
    [send],
  );
  const startGame = useCallback(
    (contentMode?: ContentMode) =>
      send(
        contentMode
          ? { type: "start_game", contentMode }
          : { type: "start_game" },
      ),
    [send],
  );
  const submitAnswer = useCallback(
    (text: string) => send({ type: "submit_answer", text }),
    [send],
  );
  const submitDesign = useCallback(
    (design: DesignSubmission) => send({ type: "submit_design", design }),
    [send],
  );
  const submitGuesses = useCallback(
    (guesses: GuessMap) => send({ type: "submit_guesses", guesses }),
    [send],
  );
  const voteFunniest = useCallback(
    (answerId: string) => send({ type: "vote_funniest", answerId }),
    [send],
  );
  const nextRound = useCallback(() => send({ type: "next_round" }), [send]);
  const endGame = useCallback(() => send({ type: "end_game" }), [send]);
  const returnToLobby = useCallback(
    () => send({ type: "return_to_lobby" }),
    [send],
  );

  const leave = useCallback(() => {
    intentionalClose.current = true;
    cleanupSocket();
    clearSession();
    setState(null);
    setPlayerId(null);
    setStatus("idle");
  }, [cleanupSocket]);

  const reconnect = useCallback(() => {
    const session = loadSession();
    if (!session) {
      setLastError({
        code: "not_in_room",
        message: "No session to restore.",
      });
      return;
    }
    openSocket(session.code, {
      type: "rejoin",
      code: session.code,
      playerId: session.playerId,
    });
  }, [openSocket]);

  // Attempt rejoin on mount if session exists.
  // intentionalClose stays false on unmount so StrictMode remount can reconnect
  // during the server's empty-room grace period.
  useEffect(() => {
    const session = loadSession();
    if (session) {
      openSocket(session.code, {
        type: "rejoin",
        code: session.code,
        playerId: session.playerId,
      });
    }
    return () => {
      cleanupSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    state,
    playerId,
    lastError,
    clearError,
    createRoom,
    joinRoom,
    setContentMode,
    startGame,
    submitAnswer,
    submitDesign,
    submitGuesses,
    voteFunniest,
    nextRound,
    endGame,
    returnToLobby,
    leave,
    reconnect,
  };
}
